import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Key, ArrowRight, AlertCircle, Loader2, CheckCircle, Sun, Moon } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export default function BetaAccessPage() {
  const navigate = useNavigate()
  const { theme, toggleTheme } = useTheme()
  const [betaToken, setBetaToken] = useState('')
  const [validating, setValidating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validated, setValidated] = useState(false)

  const handleValidate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!betaToken.trim()) return

    setValidating(true)
    setError(null)

    try {
      const response = await fetch(`${API_BASE_URL}/v1/beta/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: betaToken.trim() }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.message || 'Invalid or expired beta token')
        return
      }

      // Store beta session
      localStorage.setItem('xarlie_beta_token', data.token)
      localStorage.setItem('xarlie_beta_access', JSON.stringify({
        token_id: data.token_id,
        label: data.label,
        publisher_id: data.publisher_id,
      }))

      setValidated(true)

      // Redirect to publisher signup or dashboard after a brief delay
      setTimeout(() => {
        if (data.publisher_id) {
          // Already linked to a publisher, go to publisher login
          navigate('/publisher/login')
        } else {
          // New beta tester, go to publisher signup with beta token
          navigate('/publisher/signup?beta=true')
        }
      }, 1500)
    } catch {
      setError('Failed to validate token. Please try again.')
    } finally {
      setValidating(false)
    }
  }

  const isDark = theme === 'dark'

  return (
    <div className={`min-h-screen relative overflow-hidden ${isDark ? 'bg-black' : 'bg-white'}`}>
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 z-50 p-2 rounded-full transition-colors ${
          isDark ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
        }`}
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      {/* Background gradient */}
      <div className="absolute inset-0">
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? 'bg-indigo-500/20' : 'bg-indigo-200/40'
        }`} />
        <div className={`absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-200/40'
        }`} />
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center min-h-screen px-4">
        {/* Back to home */}
        <a
          href="/"
          className={`absolute top-4 left-4 text-sm transition-colors ${
            isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-900'
          }`}
        >
          &larr; Back to home
        </a>

        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Beta Access
            </h1>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Enter your beta access token to unlock the full Xarlie platform
            </p>
          </div>

          {/* Token form */}
          <div className={`p-6 rounded-2xl border ${
            isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200 shadow-lg'
          }`}>
            {validated ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                <p className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Access Granted
                </p>
                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Redirecting you to the platform...
                </p>
              </div>
            ) : (
              <form onSubmit={handleValidate} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Beta Access Token
                  </label>
                  <input
                    type="text"
                    value={betaToken}
                    onChange={(e) => {
                      setBetaToken(e.target.value)
                      setError(null)
                    }}
                    placeholder="xarlie_beta_..."
                    className={`w-full px-4 py-3 rounded-lg border font-mono text-sm transition-colors ${
                      isDark
                        ? 'bg-white/5 border-white/20 text-white placeholder-gray-500 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                        : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500'
                    }`}
                    autoFocus
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-500">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={validating || !betaToken.trim()}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {validating ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    <>
                      Validate Token
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Help text */}
          <p className={`text-center text-xs mt-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Don&apos;t have a beta token? Join the{' '}
            <a href="/" className="text-indigo-500 hover:text-indigo-400 underline">
              waitlist
            </a>{' '}
            to get early access.
          </p>
        </div>
      </div>
    </div>
  )
}
